/**
 * src/lib/voice/voiceActivityDetector.js — VAD for barge-in (TICKET-103 rolling floor)
 *
 * Lightweight voice-activity detection using Web Audio API's AnalyserNode.
 * Computes windowed RMS and fires onSpeechStart once per speech burst.
 *
 * State machine:
 *   silent → [RMS ≥ threshold for holdMs consecutive] → speech
 *   speech → [RMS < threshold for resetMs consecutive] → silent
 *
 *   onSpeechStart() fires on the silent→speech transition.
 *
 * TICKET-103 — Rolling noise floor (replaces one-shot calibrationMs):
 *   Pass rollingAlpha + thresholdMultiplier to activate the rolling floor path.
 *   The effective threshold = clamp(floor * multiplier, absoluteMin, maxThreshold)
 *   where floor is an EMA updated ONLY while sourceActive=true (TTS actually playing).
 *   Call vad.setSourceActive(bool) to inject the playback-active signal.
 *
 *   This kills both CEO-device failure modes:
 *     A) Calibration sampled silence → floor ≈ 0.02 → guard useless.
 *        Fix: silence frames (sourceActive=false) don't update the floor.
 *     B) Calibration sampled full volume → threshold 0.22 uncrossable.
 *        Fix: maxThreshold cap + rolling average adapts smoothly.
 *
 *   TICKET-101 back-compat: calibrationMs/onCalibrated still accepted for the
 *   one-shot path. Omit rollingAlpha to keep legacy absolute-threshold behavior.
 *
 * Tuning defaults:
 *   threshold           = 0.02    (absolute min RMS on [0,1])
 *   holdMs              = 200     (avoid triggering on transients / pops)
 *   resetMs             = 500     (pause before the detector can fire again)
 *   rollingAlpha        = (none)  (set to e.g. 0.08 to enable rolling mode)
 *   thresholdMultiplier = 2.5     (how much above floor = barge-in)
 *   maxThreshold        = 0.25    (cap so loud TTS can't make threshold uncrossable)
 *
 * Dependencies allowed (for test seams):
 *   - AudioContextCtor (defaults to window.AudioContext || window.webkitAudioContext)
 *   - rafScheduler     (defaults to requestAnimationFrame) — tests inject a manual clock
 *   - now              (defaults to performance.now) — tests inject a fake clock
 */

import { createRollingNoiseFloor } from './rollingNoiseFloor';
import { createEchoGate } from './echoCorrelation';

export function createVAD({
  stream,
  onSpeechStart,
  onSpeechEnd, // Phase 90-06 — fires on speech→silent (after resetMs). Optional.
  threshold = 0.02,
  holdMs = 120,
  resetMs = 500,
  // 2026-04-20 (session 4) — max time the VAD can stay in 'speech' state
  // before forcing a speech→silent transition. This is a safety net against
  // autoGainControl boosting ambient noise above threshold, which otherwise
  // leaves the VAD stuck in 'speech' indefinitely (root cause of the
  // "Listening..." stuck state in conversation mode). Set generously — real
  // user utterances rarely exceed 30s.
  maxSpeechMs = 30000,
  // TICKET-101 echo guard — one-shot calibration path (still active when
  // calibrationMs > 0 AND rollingAlpha is NOT provided). Kept for back-compat.
  // TICKET-103 supersedes this for barge-in use: pass rollingAlpha instead.
  calibrationMs = 0,
  thresholdMultiplier = 2,
  maxThreshold = 0.25,
  onCalibrated,
  // TICKET-103 — continuous rolling floor path.
  // When rollingAlpha is provided (e.g. 0.08), the one-shot calibration window
  // is bypassed and a continuous EMA noise floor is used instead.
  // setSourceActive(bool) must be called by the consumer to gate floor adaptation
  // to actual TTS playback — prevents the silence-sampling failure mode.
  rollingAlpha,        // number — EMA α, activates rolling mode when provided
  // thresholdMultiplier is shared between one-shot and rolling paths.
  // onFloorUpdate: optional observability callback { floor, threshold, sourceActive }
  // Called each tick (in test) / throttled (in prod) — numbers only, no PII.
  onFloorUpdate,
  // TICKET-623 — echo guard. Consulted only at the silent→speech transition
  // edge (after holdMs is already satisfied), so it never affects normal
  // speech detection when no TTS reference is playing (isLikelyEcho() fails
  // open with reason:'no_reference' until pushReferenceEnvelope() has real
  // data). onEchoSuppressed(verdict) fires when a transition is held back —
  // this is the telemetry that was missing before (stop() used to cut
  // playback with zero trace).
  onEchoSuppressed,
  AudioContextCtor,
  rafScheduler,
  cancelRaf,
  now,
} = {}) {
  if (!stream) throw new Error('createVAD: stream is required');
  if (typeof onSpeechStart !== 'function') throw new Error('createVAD: onSpeechStart must be a function');
  if (onSpeechEnd != null && typeof onSpeechEnd !== 'function') throw new Error('createVAD: onSpeechEnd must be a function');

  const Ctx = AudioContextCtor
    || (typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext));
  if (!Ctx) throw new Error('createVAD: no AudioContext available');

  const schedule = rafScheduler || ((cb) => (
    typeof requestAnimationFrame !== 'undefined' ? requestAnimationFrame(cb) : setTimeout(cb, 16)
  ));
  const cancel = cancelRaf || ((id) => {
    if (typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(id);
    else clearTimeout(id);
  });
  const clock = now || (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));

  const ctx = new Ctx();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  source.connect(analyser);

  const buffer = new Float32Array(analyser.fftSize);

  // TICKET-103: rolling floor instance (only allocated when rollingAlpha provided).
  // maxFloor for the EMA is set to maxThreshold / thresholdMultiplier so the
  // floor cap and the threshold cap are consistent: a floor at its max × multiplier
  // still equals maxThreshold exactly.
  const useRollingFloor = typeof rollingAlpha === 'number';
  const rollingFloorInst = useRollingFloor
    ? createRollingNoiseFloor({
        alpha: rollingAlpha,
        maxFloor: maxThreshold / (thresholdMultiplier || 2),
      })
    : null;

  // sourceActive: injected via setSourceActive(). Defaults false — floor won't
  // adapt until the caller (useVoiceBargeIn) confirms TTS has actually started.
  let sourceActive = false;

  // TICKET-623 — always constructed (cheap), fails open until the caller
  // feeds it a reference envelope via pushReferenceEnvelope(). No separate
  // enable flag: the kill switch lives one layer up (useVoiceBargeIn simply
  // stops calling pushReferenceEnvelope), so disabling it reverts to exactly
  // the pre-TICKET-623 behavior with zero code path difference here.
  const echoGate = createEchoGate();

  let state = 'silent'; // 'silent' | 'speech'
  let aboveThresholdSince = null;
  let belowThresholdSince = null;
  let speechStartedAt = null; // 2026-04-20 (session 4) — tracks how long we've been in 'speech' for the maxSpeechMs safety net
  let rafId = null;
  let stopped = false;
  let effectiveThreshold = threshold;
  let calibrating = !useRollingFloor && calibrationMs > 0; // one-shot path only when no rollingAlpha
  const calibSamples = [];
  const calibStart = clock();

  function tick() {
    if (stopped) return;
    analyser.getFloatTimeDomainData(buffer);

    // RMS over the buffer
    let sumSq = 0;
    for (let i = 0; i < buffer.length; i++) {
      const v = buffer[i];
      sumSq += v * v;
    }
    const rms = Math.sqrt(sumSq / buffer.length);
    const t = clock();
    echoGate.pushMic(rms, t / 1000); // TICKET-623 — t is ms (performance.now()); echoGate wants seconds

    // TICKET-103 rolling floor path: compute effective threshold from EMA.
    // Bypasses the one-shot calibration window entirely.
    if (useRollingFloor) {
      // PR-160 fix: snapshot the floor BEFORE potentially updating it.
      // If rms is a speech candidate (rms > prevFloor * thresholdMultiplier) AND
      // the floor is already seeded (prevFloor > 0), skip the EMA update for this
      // frame. Without this guard, the user's voice raises the floor and makes
      // effectiveThreshold chase the barge-in RMS before holdMs can elapse.
      // Exception: always update on the first active frame (prevFloor===0) so the
      // floor gets seeded — rms > 0*multiplier is always true for any audible input.
      const prevFloor = rollingFloorInst.getFloor();
      const isSpeechCandidate = prevFloor > 0 && rms > prevFloor * thresholdMultiplier;
      const floor = isSpeechCandidate
        ? prevFloor
        : rollingFloorInst.update(rms, sourceActive);

      // Safety gate: if the floor hasn't been seeded yet (no active playback
      // frame seen) keep the VAD unarmed. This prevents false barge-ins during
      // the window between the VAD mounting and the first TTS audio starting —
      // exactly the scenario TICKET-101 one-shot tried to handle with calibrationMs.
      // getFloor()==0 means rollingNoiseFloor.seeded is false (no update with
      // sourceActive=true has occurred yet).
      if (floor === 0) {
        rafId = schedule(tick);
        return; // unarmed — no speech detection until baseline is established
      }

      effectiveThreshold = Math.min(
        Math.max(floor * thresholdMultiplier, threshold), // at least absoluteMin
        maxThreshold                                       // at most the cap
      );
      // Spine observability — emit floor + threshold on every tick.
      // useVoiceBargeIn throttles the log to ~1 per second to avoid spam.
      if (typeof onFloorUpdate === 'function') {
        try { onFloorUpdate({ floor, threshold: effectiveThreshold, sourceActive }); } catch {
          // spine-emit-ok: observer callback — a throw must never kill the VAD rAF loop
        }
      }
    } else if (calibrating) {
      // TICKET-101 one-shot calibration path (only when rollingAlpha not set)
      if (t - calibStart < calibrationMs) {
        calibSamples.push(rms);
        rafId = schedule(tick);
        return;
      }
      calibrating = false;
      const sorted = calibSamples.slice().sort((a, b) => a - b);
      const baseline = sorted.length
        ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))]
        : 0;
      effectiveThreshold = Math.min(Math.max(threshold, baseline * thresholdMultiplier), maxThreshold);
      if (typeof onCalibrated === 'function') {
        try {
          onCalibrated({ baselineRms: baseline, effectiveThreshold, sampleCount: sorted.length });
        } catch (err) {
          // spine-emit-ok: observer callback — caller (useVoiceBargeIn) owns the spine emit; a throw here must never kill the VAD rAF loop
          console.warn('[VAD] onCalibrated threw:', err?.message);
        }
      }
    }

    if (state === 'silent') {
      if (rms >= effectiveThreshold) {
        if (aboveThresholdSince === null) aboveThresholdSince = t;
        if (t - aboveThresholdSince >= holdMs) {
          // TICKET-623 — echo guard: before promoting to 'speech', ask
          // whether this crossing is explained by the assistant's own TTS
          // (known reference, pushed via pushReferenceEnvelope). Fails open
          // (isEcho=false) when no reference is available or not enough
          // samples have accumulated yet — never blocks a real transition
          // when the guard can't confidently classify.
          const echoVerdict = echoGate.isLikelyEcho();
          if (echoVerdict.isEcho) {
            if (typeof onEchoSuppressed === 'function') {
              try {
                onEchoSuppressed(echoVerdict);
              } catch {
                // spine-emit-ok: observer callback — caller (useVoiceBargeIn) owns the spine emit; a throw here must never kill the VAD rAF loop
              }
            }
            // Re-arm the hold window rather than firing — this frame is
            // classified echo, so don't let it count toward holdMs.
            aboveThresholdSince = t;
            rafId = schedule(tick);
            return;
          }
          state = 'speech';
          aboveThresholdSince = null;
          belowThresholdSince = null;
          speechStartedAt = t;
          try { onSpeechStart(); } catch (err) { console.error('[VAD] onSpeechStart threw:', err); }
        }
      } else {
        aboveThresholdSince = null;
      }
    } else {
      // state === 'speech' — wait for sustained silence before re-arming.
      // 2026-04-20 (session 4): added maxSpeechMs safety net. When Chrome's
      // autoGainControl boosts ambient noise above threshold, the VAD can get
      // stuck in 'speech' indefinitely because sustained silence is never
      // observed. The maxSpeechMs cap forces a speech→silent transition after
      // the cap regardless of RMS, unblocking the conversation-mode turn.
      const speechTooLong = speechStartedAt != null && (t - speechStartedAt) >= maxSpeechMs;
      if (rms < effectiveThreshold || speechTooLong) {
        if (belowThresholdSince === null) belowThresholdSince = t;
        if (speechTooLong || t - belowThresholdSince >= resetMs) {
          state = 'silent';
          belowThresholdSince = null;
          speechStartedAt = null;
          if (typeof onSpeechEnd === 'function') {
            try { onSpeechEnd(); } catch (err) { console.error('[VAD] onSpeechEnd threw:', err); }
          }
        }
      } else {
        belowThresholdSince = null;
      }
    }

    rafId = schedule(tick);
  }

  rafId = schedule(tick);

  return {
    get state() { return state; },
    /**
     * TICKET-103: inject the playback-active signal from the TTS layer.
     * Call with true when an AudioBufferSource.start() fires,
     * false when the source ends or between sentence chunks.
     * The rolling floor only adapts while sourceActive=true.
     * No-op when rolling mode is not active (rollingAlpha not provided).
     */
    setSourceActive(active) { sourceActive = !!active; },
    /**
     * TICKET-623: feed the known-playing TTS reference envelope (from
     * pcmPlayer.getEnvelope()). Accepts either a single {rms,t} entry or an
     * array — useVoiceBargeIn polls pcmPlayer's ring-buffer snapshot on the
     * same interval it already uses for setSourceActive, so pushing the
     * whole snapshot each tick is simplest; duplicate entries are harmless
     * (echoGate's own ring buffer just holds a few extra points).
     */
    pushReferenceEnvelope(entryOrEntries) {
      const entries = Array.isArray(entryOrEntries) ? entryOrEntries : [entryOrEntries];
      for (const e of entries) {
        if (e && typeof e.rms === 'number' && typeof e.t === 'number') echoGate.pushReference(e.rms, e.t);
      }
    },
    stop() {
      if (stopped) return;
      stopped = true;
      if (rafId != null) cancel(rafId);
      if (rollingFloorInst) rollingFloorInst.reset();
      echoGate.reset();
      try { source.disconnect(); } catch { /* noop */ }
      try { analyser.disconnect(); } catch { /* noop */ }
      if (ctx.state !== 'closed' && typeof ctx.close === 'function') {
        try { ctx.close(); } catch { /* noop */ }
      }
    },
  };
}
